import React from "react";
import { useTheme } from "styled-components";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { tokens } from "../../activity-logs/styles";
import { BarListRow } from "../styles";
import { displayName, formatCount, formatCurrency, formatPercent } from "../dashboardData";

// Barras horizontais com nome, valor e percentual: lê-se sem eixo nem legenda
// Uma cor por barra, na ordem da paleta do design system
const colorAt = (colors, i) => colors[i % colors.length];

export function SupplierBarList({ suppliers, colors, onOpen }) {
  const max = Math.max(1, ...suppliers.map((s) => s.total));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {suppliers.map((s, i) => (
        <BarListRow key={s.id} type="button" title={s.name} onClick={() => onOpen(s)}>
          <span className="track">
            <span className="fill" style={{ width: `${(s.total / max) * 100}%`, background: colorAt(colors, i) }} />
            <span className="name">{displayName(s.name)}</span>
          </span>
          <span className="value">{formatCurrency(s.total)}</span>
          <span className="share">{formatPercent(s.share)}</span>
        </BarListRow>
      ))}
    </div>
  );
}

function BandTooltip({ active, payload }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const b = payload[0].payload;
  return (
    <div style={{
      background: tokens.solid(theme),
      border: `1px solid ${tokens.separator(theme)}`,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: tokens.label(theme),
    }}>
      <div style={{ fontWeight: 600 }}>{b.label}</div>
      <div style={{ marginTop: 4 }}>{formatCount(b.count)} {b.count === 1 ? "fornecedor" : "fornecedores"}</div>
      <div style={{ color: tokens.label2(theme), marginTop: 2 }}>{formatCurrency(b.total)} · {formatPercent(b.share)} do crédito</div>
    </div>
  );
}

// Duas linhas no eixo: a faixa e quanto ela representa do crédito
// Em tela estreita cada faixa tem pouco espaço: usa o rótulo curto (o subtítulo já diz que é em R$)
function BandTick({ x, y, payload, bands, width, visibleTicksCount }) {
  const theme = useTheme();
  const band = bands[payload.index];
  if (!band) return null;
  const slot = width / Math.max(1, visibleTicksCount || bands.length);
  const compact = slot < 92;
  return (
    <text x={x} y={y + 10} textAnchor="middle" fontSize={compact ? 10.5 : 11}>
      <tspan x={x} fill={tokens.label2(theme)}>{compact ? band.tiny : band.short}</tspan>
      <tspan x={x} dy={16} fill={tokens.label(theme)} fontWeight={600}>{formatPercent(band.share)}</tspan>
    </text>
  );
}

// Quantos fornecedores há em cada faixa, sem eixo Y: o número vem escrito na barra
export function BandChart({ bands, colors, height = 262 }) {
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={bands} margin={{ top: 22, right: 4, left: 4, bottom: 0 }}>
        <XAxis
          dataKey="short"
          interval={0}
          height={46}
          axisLine={{ stroke: tokens.separator(theme) }}
          tickLine={false}
          tick={<BandTick bands={bands} />}
        />
        <Tooltip content={<BandTooltip />} cursor={{ fill: tokens.fill(theme), radius: 8 }} />
        <Bar dataKey="count" name="Fornecedores" fillOpacity={0.7} maxBarSize={30} minPointSize={2} radius={[5, 5, 0, 0]} isAnimationActive={false}>
          {bands.map((b, i) => <Cell key={b.key} fill={colorAt(colors, i)} />)}
          <LabelList dataKey="count" position="top" formatter={formatCount} fill={tokens.label(theme)} fontSize={13} fontWeight={600} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
